import { sha256Text } from '../quote-engine/canonical-json';

export type EvalAiRetryMechanism =
    | 'rate_limit_retry'
    | 'transient_retry'
    | 'key_rotation'
    | 'truncation_retry'
    | 'empty_json_retry'
    | 'hybrid_gateway_fallback'
    | 'project_strategy_route_fallback'
    | 'risk_analysis_retry'
    | 'report_synthesis_retry'
    | 'wms_retry'
    | 'photo_forensic_fallback'
    | 'photo_forensic_recovery';

export type EvalAiAttemptDecision =
    | 'provider_request'
    | 'suppressed_retry'
    | 'blocked_duplicate_request';

export interface EvalAiLogicalStageInputV1 {
    pipelineId?: string | null;
    stageName?: string | null;
    task?: string | null;
    model?: string | null;
    route?: string | null;
    requestDigest?: string | null;
    /** Stable evaluator-owned fan-out id; hashed before it enters receipts. */
    logicalCallId?: string | null;
}

export interface EvalAiLogicalCallSlotV1 {
    /** Opaque stable id. It is hashed before entering any receipt. */
    logicalCallId: string;
    pipelineId?: string | null;
    stageName?: string | null;
    task?: string | null;
}

export interface EvalAiPaidRequestMetadataV1 {
    schemaVersion: 1;
    guardId: string;
    logicalStageId: string;
    logicalStageKey: string;
    logicalCallKey: string;
    requestSequence: number;
    requestedAt: string;
    pipelineId: string | null;
    stageName: string | null;
    task: string | null;
    model: string | null;
    route: string | null;
    requestDigest: string | null;
    parentPaidCallId: string | null;
    projectId: string | null;
    engineStage: string | null;
}

export interface EvalAiRequestProvenanceV1 {
    parentPaidCallId: string;
    projectId: string;
    engineStage: string;
}

export interface EvalAiPaidRequestCompletionV1 extends EvalAiPaidRequestMetadataV1 {
    completedAt: string;
    outcome: 'succeeded' | 'failed';
    errorCode: string | null;
}

export interface EvalAiLogicalStageHandleV1 {
    guardId: string;
    logicalStageId: string;
    logicalStageKey: string;
    logicalCallId?: string | null;
}

export interface EvalAiSingleAttemptEventV1 {
    sequence: number;
    at: string;
    decision: EvalAiAttemptDecision;
    logicalStageId: string | null;
    logicalStageKey: string | null;
    mechanism: EvalAiRetryMechanism | null;
    route: string | null;
    task: string | null;
    model: string | null;
    reason: string | null;
    requestDigest?: string | null;
}

export interface EvalAiLogicalStageReceiptV1 {
    logicalStageId: string;
    logicalStageKey: string;
    pipelineId: string | null;
    stageName: string | null;
    task: string | null;
    model: string | null;
    providerRequestCount: number;
    blockedDuplicateRequestCount: number;
}

export interface EvalAiSingleAttemptReceiptV1 {
    schemaVersion: 1;
    guardId: string;
    mode: 'quote-eval-single-attempt-ai';
    enforcementBoundary: 'browser-through-backend-to-upstream-provider';
    backendUpstreamAttemptsControlled: true;
    maxProviderRequestsPerLogicalStage: 1;
    installedAt: string;
    restoredAt: string | null;
    active: boolean;
    logicalStageCount: number;
    providerRequestCount: number;
    suppressedRetryCount: number;
    blockedDuplicateRequestCount: number;
    suppressedByMechanism: Partial<Record<EvalAiRetryMechanism, number>>;
    logicalStages: EvalAiLogicalStageReceiptV1[];
    events: EvalAiSingleAttemptEventV1[];
}

export interface EvalAiSingleAttemptOptionsV1 {
    now?: () => string;
    guardId?: string;
    beforePaidRequest?: (metadata: EvalAiPaidRequestMetadataV1) => Promise<void> | void;
    afterPaidRequest?: (metadata: EvalAiPaidRequestCompletionV1) => Promise<void> | void;
    /**
     * Arms one network-boundary permit after the durable reservation exists.
     * The returned cleanup disarms an unused permit in finally.
     */
    armProviderRequestPermit?: (
        metadata: EvalAiPaidRequestMetadataV1,
    ) => {
        /** Opaque local-only token passed to the intended transport callback. */
        token: string;
        disarm(): void;
    } | void;
}

export interface InstalledEvalAiSingleAttemptPolicyV1 {
    readonly guardId: string;
    getReceipt(): EvalAiSingleAttemptReceiptV1;
    restore(): EvalAiSingleAttemptReceiptV1;
}

interface ActivePolicyV1 {
    receipt: EvalAiSingleAttemptReceiptV1;
    now: () => string;
    nextStageSequence: number;
    stagesById: Map<string, EvalAiLogicalStageReceiptV1>;
    reservedLogicalCalls: Map<string, EvalAiPaidRequestMetadataV1>;
    beforePaidRequest?: EvalAiSingleAttemptOptionsV1['beforePaidRequest'];
    afterPaidRequest?: EvalAiSingleAttemptOptionsV1['afterPaidRequest'];
    armProviderRequestPermit?: EvalAiSingleAttemptOptionsV1['armProviderRequestPermit'];
    logicalCallSlots: EvalAiLogicalCallSlotV1[] | null;
    requestProvenance: (EvalAiRequestProvenanceV1 & { refs: string[] }) | null;
}

let activePolicy: ActivePolicyV1 | null = null;

function clean(value: unknown): string | null {
    const result = typeof value === 'string' ? value.trim() : '';
    return result || null;
}

function cleanAuditReason(value: unknown): string | null {
    const reason = clean(value);
    if (!reason) return null;
    // Retry callers often pass provider Error.message values. Those messages
    // are not trustworthy receipt content: an upstream may echo a prompt,
    // filename, URL query, or other customer data. Preserve only compact
    // machine labels and collapse everything else to a content-free marker.
    return /^[a-z0-9][a-z0-9_.:-]{0,127}$/i.test(reason)
        ? reason
        : 'runtime_failure';
}

function defaultGuardId(): string {
    try {
        if (typeof globalThis.crypto?.randomUUID === 'function') {
            return `quote-eval-ai-${globalThis.crypto.randomUUID()}`;
        }
    } catch { /* deterministic fallback below */ }
    return `quote-eval-ai-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function cloneReceipt(receipt: EvalAiSingleAttemptReceiptV1): EvalAiSingleAttemptReceiptV1 {
    return JSON.parse(JSON.stringify(receipt)) as EvalAiSingleAttemptReceiptV1;
}

function stageKey(input: EvalAiLogicalStageInputV1): string {
    return [
        clean(input.pipelineId) || 'ai-call',
        clean(input.stageName) || 'unknown-stage',
        clean(input.task) || 'unknown-task',
    ].join('/');
}

function recordEvent(
    policy: ActivePolicyV1,
    event: Omit<EvalAiSingleAttemptEventV1, 'sequence' | 'at'>,
): void {
    policy.receipt.events.push({
        sequence: policy.receipt.events.length + 1,
        at: policy.now(),
        ...event,
    });
}

export class EvalAiSingleAttemptViolationError extends Error {
    readonly code = 'QM_EVAL_AI_SINGLE_ATTEMPT_BLOCKED';
    readonly logicalStageId: string;
    readonly logicalStageKey: string;

    constructor(stage: EvalAiLogicalStageReceiptV1) {
        super(`Quote evaluation blocked a second provider request for logical AI stage ${stage.logicalStageKey}`);
        this.name = 'EvalAiSingleAttemptViolationError';
        this.logicalStageId = stage.logicalStageId;
        this.logicalStageKey = stage.logicalStageKey;
    }
}

export class EvalAiPaidRequestJournalError extends AggregateError {
    readonly code: 'QUOTE_ENGINE_EVAL_JOURNAL_FAILURE' | 'QUOTE_ENGINE_EVAL_UNKNOWN_OUTCOME';
    readonly journalPhase: 'beforePaidRequest' | 'afterPaidRequest';

    constructor(
        journalError: unknown,
        providerError?: unknown,
        journalPhase: 'beforePaidRequest' | 'afterPaidRequest' = 'afterPaidRequest',
    ) {
        super(
            providerError === undefined
                ? [journalError]
                : [journalError, providerError],
            journalPhase === 'beforePaidRequest'
                ? 'Quote evaluation could not durably reserve the paid AI request.'
                : 'Quote evaluation could not durably record the paid AI request outcome.',
            { cause: journalError },
        );
        this.name = 'EvalAiPaidRequestJournalError';
        this.journalPhase = journalPhase;
        this.code = journalPhase === 'beforePaidRequest'
            ? 'QUOTE_ENGINE_EVAL_JOURNAL_FAILURE'
            : 'QUOTE_ENGINE_EVAL_UNKNOWN_OUTCOME';
    }
}

/**
 * Process-local policy used only by the local Quote Engine evaluator. The
 * normal QuoteMaker runtime remains unchanged until this guard is installed.
 * Receipts intentionally contain routing labels only: never prompts, media,
 * provider keys, request bodies, or model responses.
 */
export function installEvalAiSingleAttemptPolicy(
    options: EvalAiSingleAttemptOptionsV1 = {},
): InstalledEvalAiSingleAttemptPolicyV1 {
    if (activePolicy) {
        throw new Error(`Quote evaluation AI single-attempt policy is already active (${activePolicy.receipt.guardId}).`);
    }
    const now = options.now || (() => new Date().toISOString());
    const guardId = options.guardId || defaultGuardId();
    const receipt: EvalAiSingleAttemptReceiptV1 = {
        schemaVersion: 1,
        guardId,
        mode: 'quote-eval-single-attempt-ai',
        enforcementBoundary: 'browser-through-backend-to-upstream-provider',
        backendUpstreamAttemptsControlled: true,
        maxProviderRequestsPerLogicalStage: 1,
        installedAt: now(),
        restoredAt: null,
        active: true,
        logicalStageCount: 0,
        providerRequestCount: 0,
        suppressedRetryCount: 0,
        blockedDuplicateRequestCount: 0,
        suppressedByMechanism: {},
        logicalStages: [],
        events: [],
    };
    const policy: ActivePolicyV1 = {
        receipt,
        now,
        nextStageSequence: 0,
        stagesById: new Map(),
        reservedLogicalCalls: new Map(),
        beforePaidRequest: options.beforePaidRequest,
        afterPaidRequest: options.afterPaidRequest,
        armProviderRequestPermit: options.armProviderRequestPermit,
        logicalCallSlots: null,
        requestProvenance: null,
    };
    activePolicy = policy;
    let restored = false;
    return {
        guardId,
        getReceipt() {
            return cloneReceipt(receipt);
        },
        restore() {
            if (!restored) {
                restored = true;
                if (activePolicy === policy) activePolicy = null;
                receipt.active = false;
                receipt.restoredAt = now();
            }
            return cloneReceipt(receipt);
        },
    };
}

export function isEvalAiSingleAttemptPolicyActive(): boolean {
    return activePolicy?.receipt.active === true;
}

function cleanProvenance(value: unknown, label: string): string {
    const cleaned = clean(value);
    if (!cleaned || !/^[A-Za-z0-9][A-Za-z0-9._:/-]{0,191}$/.test(cleaned)) {
        throw new TypeError(`Quote evaluation ${label} must be a portable opaque identifier.`);
    }
    return cleaned;
}

export function evalAiRequestRef(
    metadata: Pick<EvalAiPaidRequestMetadataV1, 'requestSequence' | 'logicalCallKey'>,
): string {
    return `ai-request:${String(metadata.requestSequence).padStart(6, '0')}:${metadata.logicalCallKey}`;
}

/** Bind every nested provider reservation to its evaluator-owned operation. */
export async function withEvalAiRequestProvenance<T>(
    provenance: EvalAiRequestProvenanceV1,
    operation: () => Promise<T>,
    onRequestRefs?: (refs: string[]) => void,
): Promise<T> {
    const policy = activePolicy;
    if (!policy) return operation();
    if (policy.requestProvenance) {
        throw new Error('Quote evaluation AI request provenance cannot be nested.');
    }
    const scope = {
        parentPaidCallId: cleanProvenance(provenance.parentPaidCallId, 'parentPaidCallId'),
        projectId: cleanProvenance(provenance.projectId, 'projectId'),
        engineStage: cleanProvenance(provenance.engineStage, 'engineStage'),
        refs: [] as string[],
    };
    policy.requestProvenance = scope;
    try {
        return await operation();
    } finally {
        if (activePolicy === policy) policy.requestProvenance = null;
        onRequestRefs?.([...scope.refs]);
    }
}

function validateLogicalCallId(value: unknown): string {
    const id = clean(value);
    if (!id || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/.test(id)) {
        throw new TypeError('Quote evaluation logicalCallId must be a portable opaque identifier.');
    }
    return id;
}

function slotSelectorKey(slot: EvalAiLogicalCallSlotV1): string {
    return JSON.stringify({
        pipelineId: Object.prototype.hasOwnProperty.call(slot, 'pipelineId')
            ? clean(slot.pipelineId)
            : '*',
        stageName: Object.prototype.hasOwnProperty.call(slot, 'stageName')
            ? clean(slot.stageName)
            : '*',
        task: Object.prototype.hasOwnProperty.call(slot, 'task')
            ? clean(slot.task)
            : '*',
    });
}

function slotMatches(slot: EvalAiLogicalCallSlotV1, input: EvalAiLogicalStageInputV1): boolean {
    const matches = (key: 'pipelineId' | 'stageName' | 'task') => (
        !Object.prototype.hasOwnProperty.call(slot, key)
        || clean(slot[key]) === clean(input[key])
    );
    return matches('pipelineId') && matches('stageName') && matches('task');
}

function scopedLogicalCallId(
    policy: ActivePolicyV1,
    input: EvalAiLogicalStageInputV1,
): string | null {
    if (!policy.logicalCallSlots) return null;
    const slot = policy.logicalCallSlots.find(candidate => slotMatches(candidate, input));
    if (!slot) {
        throw new Error(
            `Quote evaluation encountered an undeclared AI call slot (${stageKey(input)}).`,
        );
    }
    return validateLogicalCallId(slot.logicalCallId);
}

/**
 * Resolve an evaluator-declared slot to a stable id for a legacy helper that
 * legitimately fans out internally. The helper, not the request body, supplies
 * the deterministic fan-out label (for example candidate-batch-2). This keeps
 * requestDigest as evidence only while still making every paid dispatch occupy
 * an evaluator-owned logical slot.
 */
export function resolveEvalAiLogicalCallIdForSlot(
    input: EvalAiLogicalStageInputV1,
    fanoutId?: string | null,
): string | null {
    const policy = activePolicy;
    if (!policy?.logicalCallSlots) return null;
    const base = scopedLogicalCallId(policy, input);
    if (!base) return null;
    const suffix = clean(fanoutId);
    if (!suffix) return base;
    if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,79}$/.test(suffix)) {
        throw new TypeError('Quote evaluation fanoutId must be a portable opaque identifier.');
    }
    return validateLogicalCallId(`${base}:${suffix}`);
}

/**
 * Bind nested/legacy AI helpers to evaluator-owned stable call slots without
 * exposing customer request bodies. Every selector must be unique. Re-entering
 * the same selector resolves to the same id, so a fresh wrapper with a changed
 * body is still rejected; legitimate fan-out must declare distinct selectors.
 */
export async function withEvalAiLogicalCallSlots<T>(
    slots: EvalAiLogicalCallSlotV1[],
    operation: () => Promise<T>,
): Promise<T> {
    const policy = activePolicy;
    if (!policy) return operation();
    if (policy.logicalCallSlots) {
        throw new Error('Quote evaluation AI logical call slots cannot be nested.');
    }
    if (!Array.isArray(slots) || slots.length === 0) {
        throw new TypeError('Quote evaluation requires at least one logical call slot.');
    }
    const selectors = new Set<string>();
    const normalized = slots.map(slot => {
        const copy = { ...slot, logicalCallId: validateLogicalCallId(slot.logicalCallId) };
        const selector = slotSelectorKey(copy);
        if (selectors.has(selector)) {
            throw new TypeError('Quote evaluation logical call slot selectors must be unique.');
        }
        selectors.add(selector);
        return copy;
    });
    policy.logicalCallSlots = normalized;
    try {
        return await operation();
    } finally {
        if (activePolicy === policy) policy.logicalCallSlots = null;
    }
}

/** Create one logical-stage token per public AI wrapper invocation. */
export function beginEvalAiLogicalStage(
    input: EvalAiLogicalStageInputV1 = {},
): EvalAiLogicalStageHandleV1 | null {
    const policy = activePolicy;
    if (!policy) return null;
    const logicalCallId = clean(input.logicalCallId) || scopedLogicalCallId(policy, input);
    policy.nextStageSequence += 1;
    const logicalStageId = `${policy.receipt.guardId}:stage-${policy.nextStageSequence}`;
    const logicalStageKey = stageKey(input);
    const stage: EvalAiLogicalStageReceiptV1 = {
        logicalStageId,
        logicalStageKey,
        pipelineId: clean(input.pipelineId),
        stageName: clean(input.stageName),
        task: clean(input.task),
        model: clean(input.model),
        providerRequestCount: 0,
        blockedDuplicateRequestCount: 0,
    };
    policy.stagesById.set(logicalStageId, stage);
    policy.receipt.logicalStages.push(stage);
    policy.receipt.logicalStageCount += 1;
    return {
        guardId: policy.receipt.guardId,
        logicalStageId,
        logicalStageKey,
        logicalCallId,
    };
}

/**
 * Record immediately before bytes leave for a paid provider/gateway request.
 * A second request made from the same public wrapper is blocked fail-closed,
 * even if a future retry path forgets to consult the policy explicitly.
 */
export function recordEvalAiProviderRequest(
    handle: EvalAiLogicalStageHandleV1 | null | undefined,
    input: EvalAiLogicalStageInputV1 = {},
): EvalAiPaidRequestMetadataV1 | null {
    const policy = activePolicy;
    if (!policy || !handle) return null;
    if (handle.guardId !== policy.receipt.guardId) {
        throw new Error('Quote evaluation AI stage token belongs to a different active guard.');
    }
    const stage = policy.stagesById.get(handle.logicalStageId);
    if (!stage) throw new Error(`Unknown Quote evaluation AI stage token ${handle.logicalStageId}.`);
    const route = clean(input.route);
    const explicitLogicalCallId = clean(input.logicalCallId) || clean(handle.logicalCallId);
    if (explicitLogicalCallId) validateLogicalCallId(explicitLogicalCallId);
    const requestDigest = clean(input.requestDigest);
    const logicalCallKey = sha256Text(JSON.stringify(explicitLogicalCallId
        ? { logicalCallId: explicitLogicalCallId }
        : {
            pipelineId: stage.pipelineId,
            stageName: stage.stageName,
            task: clean(input.task) || stage.task,
            route,
            requestDigest,
        }));
    if (stage.providerRequestCount >= 1) {
        stage.blockedDuplicateRequestCount += 1;
        policy.receipt.blockedDuplicateRequestCount += 1;
        recordEvent(policy, {
            decision: 'blocked_duplicate_request',
            logicalStageId: stage.logicalStageId,
            logicalStageKey: stage.logicalStageKey,
            mechanism: null,
            route,
            task: clean(input.task) || stage.task,
            model: clean(input.model) || stage.model,
            reason: 'max_provider_requests_per_logical_stage_exceeded',
            requestDigest: clean(input.requestDigest),
        });
        throw new EvalAiSingleAttemptViolationError(stage);
    }
    if (policy.reservedLogicalCalls.has(logicalCallKey)) {
        stage.blockedDuplicateRequestCount += 1;
        policy.receipt.blockedDuplicateRequestCount += 1;
        recordEvent(policy, {
            decision: 'blocked_duplicate_request',
            logicalStageId: stage.logicalStageId,
            logicalStageKey: stage.logicalStageKey,
            mechanism: null,
            route,
            task: clean(input.task) || stage.task,
            model: clean(input.model) || stage.model,
            reason: 'logical_call_already_reserved',
            requestDigest,
        });
        throw new EvalAiSingleAttemptViolationError(stage);
    }
    stage.providerRequestCount += 1;
    policy.receipt.providerRequestCount += 1;
    recordEvent(policy, {
        decision: 'provider_request',
        logicalStageId: stage.logicalStageId,
        logicalStageKey: stage.logicalStageKey,
        mechanism: null,
        route,
        task: clean(input.task) || stage.task,
        model: clean(input.model) || stage.model,
        reason: null,
        requestDigest,
    });
    const metadata: EvalAiPaidRequestMetadataV1 = {
        schemaVersion: 1,
        guardId: policy.receipt.guardId,
        logicalStageId: stage.logicalStageId,
        logicalStageKey: stage.logicalStageKey,
        logicalCallKey,
        requestSequence: policy.receipt.providerRequestCount,
        requestedAt: policy.now(),
        pipelineId: stage.pipelineId,
        stageName: stage.stageName,
        task: clean(input.task) || stage.task,
        model: clean(input.model) || stage.model,
        route,
        requestDigest,
        parentPaidCallId: policy.requestProvenance?.parentPaidCallId || null,
        projectId: policy.requestProvenance?.projectId || null,
        engineStage: policy.requestProvenance?.engineStage || null,
    };
    // Reserve globally before any asynchronous journal hook or provider
    // dispatch. A higher-level retry that creates a fresh wrapper token still
    // resolves to the same content-addressed logical call and is rejected.
    policy.reservedLogicalCalls.set(logicalCallKey, metadata);
    policy.requestProvenance?.refs.push(evalAiRequestRef(metadata));
    return metadata;
}

function errorCode(error: unknown): string | null {
    if (!error || typeof error !== 'object') return null;
    const value = (error as { code?: unknown; name?: unknown }).code
        || (error as { name?: unknown }).name;
    const candidate = clean(value);
    if (!candidate) return null;
    if (['AbortError', 'Error', 'RangeError', 'TypeError'].includes(candidate)) return candidate;
    return /^(?:AI_|ERR_|HTTP_|QM_EVAL_|QUOTE_ENGINE_EVAL_)[A-Z0-9_:-]{1,120}$/.test(candidate)
        ? candidate
        : 'PROVIDER_ERROR';
}

/**
 * SHA-256 a request envelope without retaining it. Callers may pass an already
 * serialized body to avoid a second JSON traversal. The callback/receipt sees
 * only the digest.
 */
export async function digestEvalAiRequest(value: unknown): Promise<string | null> {
    let serialized: string;
    try {
        serialized = typeof value === 'string' ? value : JSON.stringify(value);
    } catch {
        return null;
    }
    if (!serialized) return null;
    try {
        if (globalThis.crypto?.subtle && typeof TextEncoder !== 'undefined') {
            const bytes = new TextEncoder().encode(serialized);
            const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
            const hex = Array.from(new Uint8Array(digest))
                .map(byte => byte.toString(16).padStart(2, '0'))
                .join('');
            return `sha256:${hex}`;
        }
    } catch { /* portable fallback below */ }
    return sha256Text(serialized);
}

/**
 * Reserve, dispatch, and finalize one paid browser->gateway/provider request.
 * The before hook is awaited before dispatch, making an append-only reservation
 * durable before billing can occur. A failed after hook stops the evaluator.
 */
export async function withEvalAiPaidRequest<T>(
    handle: EvalAiLogicalStageHandleV1 | null | undefined,
    input: EvalAiLogicalStageInputV1 & { requestPayload?: unknown },
    request: (providerRequestPermitToken?: string) => Promise<T>,
): Promise<T> {
    const policy = activePolicy;
    if (!policy || !handle) return request();
    const requestDigest = clean(input.requestDigest)
        || await digestEvalAiRequest(input.requestPayload);
    const recorded = recordEvalAiProviderRequest(handle, {
        ...input,
        requestDigest,
    });
    if (!recorded) return request();
    const metadata = Object.freeze({ ...recorded });
    try {
        await policy.beforePaidRequest?.(metadata);
    } catch (beforeError) {
        throw new EvalAiPaidRequestJournalError(beforeError, undefined, 'beforePaidRequest');
    }
    let providerRequestPermit: {
        token: string;
        disarm(): void;
    } | void;
    try {
        providerRequestPermit = policy.armProviderRequestPermit?.(metadata);
    } catch (permitError) {
        throw new EvalAiPaidRequestJournalError(permitError, undefined, 'beforePaidRequest');
    }
    let result: T;
    try {
        result = await request(providerRequestPermit?.token);
    } catch (error) {
        providerRequestPermit?.disarm();
        providerRequestPermit = undefined;
        const completion: EvalAiPaidRequestCompletionV1 = {
            ...metadata,
            completedAt: policy.now(),
            outcome: 'failed',
            errorCode: errorCode(error),
        };
        try {
            await policy.afterPaidRequest?.(completion);
        } catch (afterError) {
            throw new EvalAiPaidRequestJournalError(afterError, error);
        }
        throw error;
    } finally {
        providerRequestPermit?.disarm();
    }
    const httpStatus = Number((result as { status?: unknown } | null)?.status);
    const httpFailed = Number.isFinite(httpStatus) && (httpStatus < 200 || httpStatus >= 300);
    try {
        await policy.afterPaidRequest?.({
            ...metadata,
            completedAt: policy.now(),
            outcome: httpFailed ? 'failed' : 'succeeded',
            errorCode: httpFailed ? `HTTP_${httpStatus}` : null,
        });
    } catch (afterError) {
        throw new EvalAiPaidRequestJournalError(afterError);
    }
    return result;
}

/** Called only when a retry/fallback condition actually occurs. */
export function suppressEvalAiRetry(
    mechanism: EvalAiRetryMechanism,
    input: EvalAiLogicalStageInputV1 & {
        handle?: EvalAiLogicalStageHandleV1 | null;
        reason?: string | null;
    } = {},
): boolean {
    const policy = activePolicy;
    if (!policy) return false;
    const handle = input.handle || null;
    const stage = handle?.guardId === policy.receipt.guardId
        ? policy.stagesById.get(handle.logicalStageId) || null
        : null;
    policy.receipt.suppressedRetryCount += 1;
    policy.receipt.suppressedByMechanism[mechanism] =
        (policy.receipt.suppressedByMechanism[mechanism] || 0) + 1;
    recordEvent(policy, {
        decision: 'suppressed_retry',
        logicalStageId: stage?.logicalStageId || null,
        logicalStageKey: stage?.logicalStageKey || (stageKey(input)),
        mechanism,
        route: clean(input.route),
        task: clean(input.task) || stage?.task || null,
        model: clean(input.model) || stage?.model || null,
        reason: cleanAuditReason(input.reason),
        requestDigest: null,
    });
    return true;
}

/** Collapse a configured retry/key-rotation loop to its first attempt only. */
export function evalAiAttemptLimit(configuredAttempts: number): number {
    if (!isEvalAiSingleAttemptPolicyActive()) return configuredAttempts;
    return Math.min(1, Math.max(0, configuredAttempts));
}
